import React, { useEffect, useState } from 'react'
import { Form, Button, Modal, Toast } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import FormContainer from '../components/FormContainer'
import Meta from '../components/Meta'
import CheckoutSteps from '../components/CheckoutSteps'
import { saveShippingAddress } from '../slices/cartSlice'
import { validateUSPSAddress } from '../slices/shippingSlice'
import { useDispatch as useReduxDispatch, useSelector as useReduxSelector } from 'react-redux'
import axios from 'axios'
import { setUserInfo } from '../slices/userSlice'
import {
  sanitizeEmail,
  sanitizeName,
  sanitizeAddressLine,
  sanitizeCity,
  sanitizeState,
  sanitizePostalCode,
  sanitizeCountry,
  normalizeCountryToISO,
  getPostalPatternAndTitle,
  formatPostalCode,
  sanitizeStateStrict,
} from '../utils/sanitize'


const ShippingScreen = ({ history, props }) => {
 
  const cart = useSelector((state) => state.cart)
  const { shippingAddress } = cart
  const safeShipping = shippingAddress || {}

  // Support both RTK user slice and legacy userLogin slice
  const userInfo = useSelector((state) => state.user?.userInfo || state.userLogin?.userInfo || null)
  const isGuest = !!userInfo?.isGuest

  const [shipToFirstName, setShipToFirstName] = useState(safeShipping.shipToFirstName || '')
  const [shipToLastName, setShipToLastName] = useState(safeShipping.shipToLastName || '')
  const [email, setEmail] = useState(isGuest ? '' : (userInfo?.email || ''))
  const [address1, setAddress1] = useState(safeShipping.address1 || '')
  const [address2, setAddress2] = useState(safeShipping.address2 || '')
  const [city, setCity] = useState(safeShipping.city || '')
  const [postalCode, setPostalCode] = useState(safeShipping.postalCode || '')
  const [state, setState] = useState(safeShipping.state || '')
  const [country, setCountry] = useState(safeShipping.country || '')
  const [addresses, setAddresses] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [verified, setVerified] = useState(false);
  const [profileError, setProfileError] = useState(null)
  const [addressValidationError, setAddressValidationError] = useState(null)
  const [promoOptIn, setPromoOptIn] = useState(false)
  // One-time guard to prevent multiple profile updates after verification
  const ranUpdateRef = React.useRef(false);

  const handleClose = () => {
    setShowModal(false);
  }

  const reduxDispatch = useReduxDispatch()
  const rootUserInfo = useReduxSelector(s => s.user?.userInfo)
  useEffect(() => {
    if (!rootUserInfo && typeof document !== 'undefined' && document.cookie.includes('guestToken=')) {
      axios.get('/api/users/session', { withCredentials: true })
        .then(r => { reduxDispatch(setUserInfo(r.data)); try { localStorage.setItem('userInfo', JSON.stringify(r.data)) } catch {} })
        .catch(() => {})
    }
  }, [rootUserInfo, reduxDispatch])

  const dispatch = useDispatch()

const { shippingAddressRes, errorAddress } = useSelector((state) => state.shipping)

    function setAddress(shippingAddressRes)  {
        setAddresses(shippingAddressRes.AddressValidateResponse.Address)

      }

    useEffect(() => {
      if(shippingAddressRes) {setAddress(shippingAddressRes)}
    }, [shippingAddressRes]);

    
  const handleSubmit = async () => {
  setAddress1(sanitizeAddressLine(selectedAddress.Address2))
  setAddress2(sanitizeAddressLine(selectedAddress.Address1))
  setCity(sanitizeCity(selectedAddress.City))
  {
    const iso = normalizeCountryToISO(country)
    setState(sanitizeStateStrict(selectedAddress.State, { country: iso, strict: iso === 'US' }))
    setPostalCode(formatPostalCode(selectedAddress.Zip5, country))
  }
    setVerified(true)
    setShowModal(false)
  }
  useEffect(() => {
    if (!verified || ranUpdateRef.current) return;
    ranUpdateRef.current = true;

    const updateGuestProfileIfNeeded = async () => {
      try {
        // Update profile for both guests and logged-in users
        // Build request body without changing header name; include ship-to names within shippingAddress
        const body = {
          shippingAddress: {
            address1: sanitizeAddressLine(address1),
            address2: sanitizeAddressLine(address2),
            city: sanitizeCity(city),
            postalCode: sanitizePostalCode(postalCode),
            state: sanitizeState(state),
            country: sanitizeCountry(country),
            shipToFirstName: sanitizeName(shipToFirstName),
            shipToLastName: sanitizeName(shipToLastName),
          },
        };
        // Email only relevant for guests
        if (isGuest) {
          body.email = sanitizeEmail(email);
        }

        const { data: updated } = await axios.put('/api/users/profile', body, {
          withCredentials: true,
          headers: { Authorization: userInfo?.token ? `Bearer ${userInfo.token}` : undefined }
        })
        // Preserve header name: overwrite names with existing header values
        const newInfo = { ...userInfo, ...updated, firstName: userInfo?.firstName, lastName: userInfo?.lastName }
        dispatch(setUserInfo(newInfo))
        localStorage.setItem('userInfo', JSON.stringify(newInfo))
        try {
          const subEmail = (email||'').trim().toLowerCase()
          if (promoOptIn && subEmail && /[^\s@]+@[^\s@]+\.[^\s@]+/.test(subEmail)) {
            await axios.post('/api/mailing-list/subscribe', { email: subEmail, source: 'shipping_opt_in' }, {
              withCredentials: true,
              headers: { Authorization: userInfo?.token ? `Bearer ${userInfo.token}` : undefined }
            }).catch(()=>{})
          }
        } catch {}
      } catch (e) {
        console.log('Failed to update user profile with shipping', e)
        setProfileError(e?.response?.data?.message || 'Failed to update profile')
      }
      dispatch(saveShippingAddress({ shipToFirstName, shipToLastName, address1, address2, city, postalCode, state, country, email }))
      history.push(`/payment`)
    }

    updateGuestProfileIfNeeded()
  }, [
    verified,
    shipToFirstName, shipToLastName, email,
    address1, address2, city, postalCode, state, country,
    dispatch, userInfo, history,
    isGuest, promoOptIn
  ])
 
  const submitHandler = (e) => {
    e.preventDefault()
    // sanitize all values prior to validation and submission
    const iso = normalizeCountryToISO(country)
    const clean = {
      shipToFirstName: sanitizeName(shipToFirstName),
      shipToLastName: sanitizeName(shipToLastName),
      email: sanitizeEmail(email),
      address1: sanitizeAddressLine(address1),
      address2: sanitizeAddressLine(address2),
      city: sanitizeCity(city),
      postalCode: formatPostalCode(postalCode, country),
      state: sanitizeStateStrict(state, { country: iso, strict: iso === 'US' }),
      country: sanitizeCountry(country),
    }

    setShipToFirstName(clean.shipToFirstName)
    setShipToLastName(clean.shipToLastName)
    setEmail(clean.email)
    setAddress1(clean.address1)
    setAddress2(clean.address2)
    setCity(clean.city)
    setPostalCode(clean.postalCode)
    setState(clean.state)
    setCountry(clean.country)

    dispatch(
      validateUSPSAddress({
        address1: clean.address1,
        address2: clean.address2,
        city: clean.city,
        postalCode: clean.postalCode,
        state: clean.state,
        country: clean.country,
      })
    )
    setShowModal(true)
  }

  const handleSelect = (addresses) => {
    setSelectedAddress(addresses);
  }

  useEffect(() => {
    if (errorAddress) {
      setAddressValidationError(typeof errorAddress === 'string' ? errorAddress : 'Address validation failed')
    }
  }, [errorAddress])

  // Prefill recipient first/last name from user profile for non-guest users when blank
  useEffect(() => {
    if (isGuest) return
    if (!shipToFirstName && userInfo?.firstName) setShipToFirstName(sanitizeName(userInfo.firstName))
    if (!shipToLastName && userInfo?.lastName) setShipToLastName(sanitizeName(userInfo.lastName))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, isGuest])

  // Prefill from preferred saved address for non-guest users when no address entered yet
  useEffect(() => {
    const prefillFromPreferred = async () => {
      try {
        if (isGuest) return
        // If an address is already present in state (e.g., user typed or cart persisted), do not overwrite
        const hasLocal = !!(address1 || address2 || city || state || postalCode || country)
        if (hasLocal) return
        const config = {
          withCredentials: true,
          headers: { Authorization: userInfo?.token ? `Bearer ${userInfo.token}` : undefined }
        }
        const { data } = await axios.get('/api/users/addresses', config)
        const list = data?.addresses || []
        const prefId = data?.preferredShippingAddressId || null
        const preferred = list.find(a => String(a._id) === String(prefId)) || null
        if (preferred) {
          setAddress1(sanitizeAddressLine(preferred.address1||''))
          setAddress2(sanitizeAddressLine(preferred.address2||''))
          setCity(sanitizeCity(preferred.city||''))
          const iso = normalizeCountryToISO(preferred.country||'')
          setState(sanitizeStateStrict(preferred.state||'', { country: iso, strict: iso === 'US' }))
          setPostalCode(formatPostalCode(preferred.postalCode||'', preferred.country||''))
          setCountry(sanitizeCountry(preferred.country||''))
        }
      } catch (_) { /* non-fatal */ }
    }
    prefillFromPreferred()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, userInfo])

  return (
    <FormContainer>
      <Meta title='Shipping — lotustor.com' robots='noindex,follow' />
      {/* For guest users, do not render the Sign In step link to avoid /login navigation */}
      <CheckoutSteps step1={!isGuest} step2 />
      <h1>Shipping</h1>
      <Form onSubmit={submitHandler}>

        <Form.Group controlId='guestEmail'>
          <Form.Label>Email</Form.Label>
          <Form.Control
            type='email'
            placeholder='Enter email for order updates'
            value={email}
            required
            inputMode='email'
            autoComplete='email'
            maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={(e) => setEmail(sanitizeEmail(e.target.value))}
          />
          <small className='text-muted'>We'll only use your email to send you details about this order.</small>
          <Form.Check id='promoOptIn' type='checkbox' label='Send me promotions & deals' className='mt-2'
            checked={promoOptIn} onChange={e => setPromoOptIn(e.target.checked)} />
        </Form.Group>

        <Form.Group controlId='toFirstName'>
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type='text'
            placeholder='First Name'
            value={shipToFirstName}
            required
            autoComplete='given-name'
            maxLength={64}
            onChange={(e) => setShipToFirstName(e.target.value)}
            onBlur={(e) => setShipToFirstName(sanitizeName(e.target.value))}
          ></Form.Control>
        </Form.Group>
        
        <Form.Group controlId='toLastName'>
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter Last Name'
            value={shipToLastName}
            required
            autoComplete='family-name'
            maxLength={64}
            onChange={(e) => setShipToLastName(e.target.value)}
            onBlur={(e) => setShipToLastName(sanitizeName(e.target.value))}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId='address1'>
          <Form.Label>Address 1</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter address'
            value={address1}
            required
            autoComplete='address-line1'
            maxLength={100}
            onChange={(e) => setAddress1(e.target.value)}
            onBlur={(e) => setAddress1(sanitizeAddressLine(e.target.value))}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId='address2'>
          <Form.Label>Address 2</Form.Label>
          <Form.Control
            type='text'
            placeholder='apt, suite, etc...'
            value={address2}
            autoComplete='address-line2'
            maxLength={100}
            onChange={(e) => setAddress2(e.target.value)}
            onBlur={(e) => setAddress2(sanitizeAddressLine(e.target.value))}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId='city'>
          <Form.Label>City</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter city'
            value={city}
            required
            autoComplete='address-level2'
            maxLength={64}
            onChange={(e) => setCity(e.target.value)}
            onBlur={(e) => setCity(sanitizeCity(e.target.value))}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId='state'>
          <Form.Label>State</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter state'
            value={state}
            required
            autoComplete='address-level1'
            maxLength={32}
            onChange={(e) => setState(e.target.value)}
            onBlur={(e) => {
              const iso = normalizeCountryToISO(country)
              setState(sanitizeStateStrict(e.target.value, { country: iso, strict: iso === 'US' }))
            }}
          ></Form.Control>
        </Form.Group>

        <Form.Group controlId='postalCode'>
          <Form.Label>Postal Code</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter postal code'
            value={postalCode}
            required
            inputMode={(() => { const iso = normalizeCountryToISO(country); return (iso === 'US' || iso === 'AU') ? 'numeric' : 'text' })()}
            autoComplete='postal-code'
            maxLength={10}
            pattern={getPostalPatternAndTitle(country).pattern}
            title={getPostalPatternAndTitle(country).title}
            onChange={(e) => setPostalCode(e.target.value)}
            onBlur={(e) => setPostalCode(formatPostalCode(e.target.value, country))}
          ></Form.Control>
        </Form.Group>

        

        <Form.Group controlId='country'>
          <Form.Label>Country</Form.Label>
          <Form.Control
            type='text'
            placeholder='Enter country'
            value={country}
            required
            autoComplete='country-name'
            maxLength={56}
            onChange={(e) => setCountry(e.target.value)}
            onBlur={(e) => setCountry(sanitizeCountry(e.target.value))}
          ></Form.Control>
        </Form.Group>

        <Button type='submit' variant='primary'>
          Continue
        </Button>
        {/* <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Select Shipping Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addresses.map(address => (
            <div key={address.Zip5}>
              <input
                type="radio"
                name="shipping-address"
                value={address.Zip5}
                onChange={() => handleSelect(address.Address)}
              />
              {addresses.Address2}, {addresses.City}, {addresses.State} {addresses.Zip}
            </div>
          ))}
          </Modal.Body>
          </Modal> */}
          <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
        <Modal.Title>Select Shipping Address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        {addresses && (
        <div key={addresses.Zip5}>
        <input
        type="radio"
        name="shipping-address"
        value={addresses.Zip5}
        onChange={() => handleSelect(addresses)}
        />
        <div></div>
        {addresses.Address2}, {addresses.Address1}, {addresses.City}, {addresses.State}, {addresses.Zip5}
        </div>
        )}
        </Modal.Body>
        <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
        Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!selectedAddress}>
        Save
        </Button>
        </Modal.Footer>
        </Modal>
      </Form>
      {/* Toasts */}
      <div aria-live='assertive' aria-atomic='true' style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1060 }}>
        <Toast bg='danger' onClose={() => setProfileError(null)} show={!!profileError} delay={6000} autohide>
          <Toast.Header><strong className='me-auto'>Profile Error</strong></Toast.Header>
          <div className='toast-body text-white'>{profileError}</div>
        </Toast>
        <Toast bg='danger' onClose={() => setAddressValidationError(null)} show={!!addressValidationError} delay={6000} autohide>
          <Toast.Header><strong className='me-auto'>Address Error</strong></Toast.Header>
          <div className='toast-body text-white'>{addressValidationError}</div>
        </Toast>
      </div>
    </FormContainer>
    
  )
}

export default ShippingScreen
